import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import type { ClerkSecondFactorStrategy, IClerkSecondFactor, IClerkSignInResult, IClerkSignUpResult, IClerkSocialProvider, IClerkUser } from '../models/auth.models';

type ClerkConstructor = new (publishableKey: string) => any;
type ClerkGetTokenOptions = { skipCache?: boolean };

type ClerkMode = 'sign-in' | 'sign-up';
type ClerkRoutingMode = 'path' | 'hash';

const CLERK_HANDSHAKE_PARAMS = ['__clerk_handshake', '__clerk_handshake_nonce', '__clerk_help'] as const;
const CLERK_STORAGE_PREFIXES = ['__clerk_', 'clerk_'] as const;
const CLERK_COOKIE_PREFIXES = ['__clerk_', '__client_uat', '__session'] as const;
const CLERK_SESSION_STORAGE_KEYS = ['auth2.login.clerk-mode'] as const;

export interface IClerkMountOptions {
  routing?: ClerkRoutingMode;
  signInUrl?: string;
  signUpUrl?: string;
  // Used only when the current URL does not already provide a redirect_url for Clerk to honor.
  fallbackRedirectUrl?: string;
  // Set this only when a flow must ignore redirect_url and always land on a fixed route.
  forceRedirectUrl?: string;
}

interface IClerkLoadContext {
  signInUrl: string;
  signUpUrl: string;
  afterSignInUrl: string;
  afterSignUpUrl: string;
  signature: string;
}

interface IResolvedClerkMountOptions {
  routing: ClerkRoutingMode;
  signInUrl: string;
  signUpUrl: string;
  fallbackRedirectUrl: string;
  forceRedirectUrl?: string;
}

@Injectable({ providedIn: 'root' })
export class ClerkAuthService {
  private clerkInstance: any = null;
  private clerkLoadPromise: Promise<any> | null = null;
  private clerkListenersAttached = false;
  private publishableKey = '';
  private allowedRedirectOrigins: string[] = [];
  private currentLoadSignature = '';
  private isActivatingCreatedSession = false;

  private readonly errorMessageState = signal<string | null>(null);
  readonly errorMessage = this.errorMessageState.asReadonly();

  private readonly isReadyState = signal(false);
  readonly isReady = this.isReadyState.asReadonly();

  private readonly isSignedInState = signal(false);
  readonly isSignedIn = this.isSignedInState.asReadonly();

  private readonly currentUserIdState = signal<string | null>(null);
  readonly currentUserId = this.currentUserIdState.asReadonly();

  private readonly currentSessionIdState = signal<string | null>(null);
  readonly currentSessionId = this.currentSessionIdState.asReadonly();

  private readonly currentUnsafeMetadataState = signal<Record<string, unknown>>({});
  readonly currentUnsafeMetadata = this.currentUnsafeMetadataState.asReadonly();

  private readonly isActorSessionState = signal(false);
  readonly isActorSession = this.isActorSessionState.asReadonly();
  private readonly hasReturnTicketState = signal(Boolean(localStorage.getItem('scl_impersonation_return_ticket')));
  readonly hasReturnTicket = this.hasReturnTicketState.asReadonly();

  configure(publishableKey: string | null | undefined, allowedRedirectOrigins?: string[] | null): void {
    const nextKey = publishableKey?.trim() ?? '';
    if (nextKey === this.publishableKey) {
      return;
    }

    this.publishableKey = nextKey;
    this.allowedRedirectOrigins = allowedRedirectOrigins ?? [];
    this.resetRuntimeState();
  }

  async restoreSession(): Promise<boolean> {
    const clerk = await this.load();
    this.maybeActivateCreatedSession(clerk);
    this.syncState(clerk);
    return Boolean(clerk.session);
  }

  async getToken(options?: ClerkGetTokenOptions): Promise<string | null> {
    // Token reads stay on the browser Clerk SDK and do not hydrate the full profile used elsewhere in the app.
    const clerk = await this.load();

    if (!clerk.session) {
      if (options?.skipCache) {
        console.error('Attempted to get a fresh Clerk token with skipCache: true, but no active session was found.');
      }

      return null;
    }

    if (options?.skipCache) {
      console.error('Requesting a fresh Clerk token with skipCache: true before replaying the API request.');
    }

    try {
      const tokenOptions = options?.skipCache ? { skipCache: true } : undefined;
      const token = await clerk.session.getToken(tokenOptions);

      if (options?.skipCache) {
        console.error(
          token ? 'force-refresh-succeeded' : 'force-refresh-failed',
          token
            ? 'Clerk returned a fresh bearer token after bypassing the local SDK cache.'
            : 'Clerk did not return a bearer token after bypassing the local SDK cache.'
        );
      }

      return token;
    } catch (error) {
      if (options?.skipCache) {
        console.error(
          'force-refresh-failed',
          this.getErrorMessage(error, 'Clerk token refresh failed while bypassing the SDK cache.')
        );
      }

      throw new Error(this.getErrorMessage(error, 'Unable to read the Clerk bearer token.'));
    }
  }

  async mountSignIn(targetNode: HTMLDivElement, options?: IClerkMountOptions): Promise<void> {
    const resolvedOptions = this.resolveMountOptions('sign-in', options);
    const clerk = await this.load(resolvedOptions);

    clerk.mountSignIn(targetNode, {
      routing: resolvedOptions.routing,
      signUpUrl: resolvedOptions.signUpUrl,
      fallbackRedirectUrl: resolvedOptions.fallbackRedirectUrl,
      ...(resolvedOptions.forceRedirectUrl ? { forceRedirectUrl: resolvedOptions.forceRedirectUrl } : {})
    });
  }

  async mountSignUp(targetNode: HTMLDivElement, options?: IClerkMountOptions): Promise<void> {
    const resolvedOptions = this.resolveMountOptions('sign-up', options);
    const clerk = await this.load(resolvedOptions);

    clerk.mountSignUp(targetNode, {
      routing: resolvedOptions.routing,
      signInUrl: resolvedOptions.signInUrl,
      fallbackRedirectUrl: resolvedOptions.fallbackRedirectUrl,
      ...(resolvedOptions.forceRedirectUrl ? { forceRedirectUrl: resolvedOptions.forceRedirectUrl } : {})
    });
  }

  unmountSignIn(targetNode: HTMLDivElement): void {
    this.clerkInstance?.unmountSignIn(targetNode);
  }

  unmountSignUp(targetNode: HTMLDivElement): void {
    this.clerkInstance?.unmountSignUp(targetNode);
  }

  async signOut(options?: { redirectUrl?: string | null }): Promise<void> {
    const clerk = await this.load();

    if (options?.redirectUrl === null) {
      await clerk.signOut();
    } else if (typeof options?.redirectUrl === 'string' && options.redirectUrl.trim().length > 0) {
      await clerk.signOut({ redirectUrl: options.redirectUrl.trim() });
    } else {
      await clerk.signOut({ redirectUrl: this.buildAppUrl('/login') });
    }

    this.syncState(clerk);
  }

  async resetState(): Promise<void> {

    try {
      await this.signOut({ redirectUrl: null });
    } catch {
      // Best-effort reset: browser-side Clerk state should still be cleared.
    }

    this.clearBrowserState();
    this.resetRuntimeState();
  }

  async updateUnsafeMetadata(patch: Record<string, unknown>): Promise<Record<string, unknown>> {
    const clerk = await this.load();

    if (!clerk.user) {
      throw new Error('No active Clerk user is available. Complete sign in or sign up before saving metadata.');
    }

    const currentMetadata = this.readUnsafeMetadata(clerk.user.unsafeMetadata);
    const updatedUser = await clerk.user.update({
      unsafeMetadata: {
        ...currentMetadata,
        ...patch
      }
    });

    const metadata = this.readUnsafeMetadata(updatedUser.unsafeMetadata);
    this.currentUserIdState.set(updatedUser.id);
    this.currentSessionIdState.set(clerk.session?.id ?? null);
    this.currentUnsafeMetadataState.set(metadata);
    this.isSignedInState.set(Boolean(clerk.session));

    return metadata;
  }

  async resetAndRedirectToSignIn(): Promise<void> {

    const redirectUrl = this.toAppNavigationPath(window.location.href, '') ? window.location.href : null;
    const signInUrl = this.buildNavigationUrl('/login', 'sign-in', {
      preserveRedirectUrl: true,
      redirectUrl
    });

    try {
      const clerk = await this.load();
      this.isSignedInState.set(false);

      if (clerk.session) {
        await clerk.signOut({ redirectUrl: signInUrl });
        return;
      }
    } catch {
    }

    window.location.assign(signInUrl);
  }

  buildNavigationUrl(
    path: string,
    hash?: string,
    options?: { preserveRedirectUrl?: boolean; redirectUrl?: string | null }
  ): string {
    const url = new URL(path, document.baseURI);
    url.hash = hash ? hash.replace(/^#/, '') : '';

    if (options?.preserveRedirectUrl) {
      const redirectUrl = this.getSafeRedirectUrl(options.redirectUrl ?? this.getCurrentRedirectUrl());

      if (redirectUrl) {
        url.searchParams.set('redirect_url', redirectUrl);
      }
    }

    return url.toString();
  }

  getCurrentRedirectUrl(): string | null {
    return this.getSafeRedirectUrl(new URL(window.location.href).searchParams.get('redirect_url'));
  }

  normalizeCurrentRedirectUrl(): void {
    const currentUrl = new URL(window.location.href);
    const rawRedirectUrl = currentUrl.searchParams.get('redirect_url');
    const safeRedirectUrl = this.getSafeRedirectUrl(rawRedirectUrl);

    if (!rawRedirectUrl || !safeRedirectUrl || rawRedirectUrl === safeRedirectUrl) {
      return;
    }

    currentUrl.searchParams.set('redirect_url', safeRedirectUrl);
    window.history.replaceState(window.history.state, '', currentUrl.toString());
  }

  getPostAuthNavigationPath(fallbackPath = '/dashboard'): string {
    return this.toAppNavigationPath(this.getCurrentRedirectUrl(), fallbackPath);
  }

  async navigatePostAuth(router: Router, fallbackPath = '/dashboard'): Promise<void> {
    const rawRedirectUrl = new URL(window.location.href).searchParams.get('redirect_url')?.trim();

    if (rawRedirectUrl) {
      const sameOriginPath = this.toAppNavigationPath(rawRedirectUrl, '');
      if (sameOriginPath) {
        await router.navigateByUrl(sameOriginPath);
        return;
      }

      // Cross-origin redirect: validate against trusted origins
      const safeCrossOriginUrl = this.getSafeRedirectUrl(rawRedirectUrl);
      if (safeCrossOriginUrl) {
        window.location.assign(safeCrossOriginUrl);
        return;
      }
    }

    await router.navigateByUrl(fallbackPath);
  }

  getCurrentPostAuthRedirectUrl(): string | null {
    const postAuthPath = this.toAppNavigationPath(this.getCurrentRedirectUrl(), '');

    return postAuthPath
      ? this.buildAppUrl(postAuthPath)
      : null;
  }

  getSafeRedirectUrl(rawUrl?: string | null): string | null {
    const normalizedUrl = rawUrl?.trim();

    if (!normalizedUrl) {
      return null;
    }

    try {
      const parsedUrl = new URL(normalizedUrl, document.baseURI);
      const isTrustedOrigin = parsedUrl.origin === window.location.origin
        || this.allowedRedirectOrigins.includes(parsedUrl.origin);
      return isTrustedOrigin ? parsedUrl.toString() : null;
    } catch {
      return null;
    }
  }

  cleanHandshakeParams(url: string): string | null {
    const parsed = new URL(url, document.baseURI);
    const hasHandshakeParam = CLERK_HANDSHAKE_PARAMS.some((param) => parsed.searchParams.has(param));

    if (!hasHandshakeParam) {
      return null;
    }

    for (const param of CLERK_HANDSHAKE_PARAMS) {
      parsed.searchParams.delete(param);
    }

    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  }

  toAppNavigationPath(redirectUrl?: string | null, fallbackPath = '/profile'): string {
    const safeRedirectUrl = this.getSafeRedirectUrl(redirectUrl);

    if (!safeRedirectUrl) {
      return fallbackPath;
    }

    const parsedUrl = new URL(safeRedirectUrl);

    // Cross-origin URLs cannot be converted to local navigation paths
    if (parsedUrl.origin !== window.location.origin) {
      return fallbackPath;
    }

    const navigationPath = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;

    if (this.isGuestRoute(parsedUrl.pathname)) {
      return fallbackPath;
    }

    return navigationPath || fallbackPath;
  }

  // ---------------------------------------------------------------------------
  // Programmatic sign-in API (client-side only, no mounted UI)
  // ---------------------------------------------------------------------------

  /**
   * Signs in with email/password using the Clerk SDK's `signIn.create()` API.
   * On success, activates the new session and syncs local state.
   */
  async signInWithPassword(identifier: string, password: string): Promise<IClerkSignInResult> {
    const clerk = await this.load();

    try {
      const signIn = await clerk.client.signIn.create({
        identifier,
        password,
        strategy: 'password'
      });

      if (signIn.status === 'complete' && signIn.createdSessionId) {
        await clerk.setActive({ session: signIn.createdSessionId });
        this.syncState(clerk);
      }

      return this.mapSignInResult(signIn);
    } catch (error) {
      // Preserve the original Clerk error codes so callers can inspect them (e.g., form_identifier_not_found)
      const wrapped = new Error(this.getErrorMessage(error, 'Unable to sign in. Please try again.'));
      (wrapped as any).clerkErrors = (error as any)?.errors;
      throw wrapped;
    }
  }

  private static readonly RETURN_TICKET_KEY = 'scl_impersonation_return_ticket';

  /**
   * Signs in with a Clerk actor token (ticket) for impersonation.
   * The resulting session JWT will contain an `act` claim identifying the admin.
   */
  async signInWithTicket(ticket: string): Promise<void> {
    const clerk = await this.load();

    // End the current admin session without triggering Clerk's full sign-out redirect
    await this.endSessionWithoutRedirect(clerk);

    const signIn = await clerk.client.signIn.create({
      strategy: 'ticket',
      ticket
    });

    if (signIn.status !== 'complete' || !signIn.createdSessionId) {
      throw new Error('Failed to activate impersonation session.');
    }

    await clerk.setActive({ session: signIn.createdSessionId });
    this.hasReturnTicketState.set(Boolean(this.readReturnTicket()));
    this.syncState(clerk);
  }

  /**
   * Ends the impersonated session and signs back in as the admin using
   * the return ticket that was stored at impersonation time.
   * Returns `true` if the admin session was restored, `false` if the admin must re-login.
   */
  async returnFromImpersonation(): Promise<boolean> {
    const clerk = await this.load();
    const returnTicket = this.readReturnTicket();

    if (!returnTicket) {
      await this.endSessionWithoutRedirect(clerk);
      this.syncState(clerk);
      return false;
    }

    // End impersonation session without triggering Clerk's full sign-out redirect
    await this.endSessionWithoutRedirect(clerk);

    // Prevent maybeActivateCreatedSession (fired by the Clerk listener after
    // signIn.create resolves) from starting a competing setActive with a
    // redirectUrl that would navigate the page away before the caller can
    // run getUserInfo().
    this.isActivatingCreatedSession = true;
    try {
      const signIn = await clerk.client.signIn.create({
        strategy: 'ticket',
        ticket: returnTicket
      });

      if (signIn.status === 'complete' && signIn.createdSessionId) {
        await clerk.setActive({ session: signIn.createdSessionId });
        this.removeReturnTicket();
        this.syncState(clerk);
        return true;
      }
    } catch {
      // Return ticket expired or invalid — fall through
    } finally {
      this.isActivatingCreatedSession = false;
    }

    this.removeReturnTicket();
    this.syncState(clerk);
    return false;
  }

  private readReturnTicket(): string | null {
    return localStorage.getItem(ClerkAuthService.RETURN_TICKET_KEY);
  }

  private removeReturnTicket(): void {
    localStorage.removeItem(ClerkAuthService.RETURN_TICKET_KEY);
    this.hasReturnTicketState.set(false);
  }

  /** Ends the current Clerk session locally without triggering a full page redirect. */
  private async endSessionWithoutRedirect(clerk: any): Promise<void> {
    if (!clerk.session) {
      return;
    }

    try {
      await clerk.session.end();
    } catch {
      // session.end() may not be supported for actor sessions —
      // sign out without a redirect URL so the SDK clears its state
      // without navigating the page.
      await clerk.signOut();
    }
  }

  /**
   * Prepares the second factor verification (e.g., sends SMS code).
   * Call this after signInWithPassword returns `needs_second_factor`.
   */
  async prepareSignInSecondFactor(
    strategy: ClerkSecondFactorStrategy,
    phoneNumberId?: string
  ): Promise<IClerkSignInResult> {
    const clerk = await this.load();
    const signIn = clerk.client.signIn;

    const params: Record<string, string> = { strategy };
    if (strategy === 'phone_code' && phoneNumberId) {
      params['phoneNumberId'] = phoneNumberId;
    }

    const result = await signIn.prepareSecondFactor(params);
    return this.mapSignInResult(result);
  }

  /**
   * Attempts the second factor verification with the user-supplied code.
   * On success, activates the session and syncs local state.
   */
  async attemptSignInSecondFactor(
    strategy: ClerkSecondFactorStrategy,
    code: string
  ): Promise<IClerkSignInResult> {
    const clerk = await this.load();
    const signIn = clerk.client.signIn;

    const result = await signIn.attemptSecondFactor({ strategy, code });

    if (result.status === 'complete' && result.createdSessionId) {
      await clerk.setActive({ session: result.createdSessionId });
      this.syncState(clerk);
    }

    return this.mapSignInResult(result);
  }

  // ---------------------------------------------------------------------------
  // Programmatic sign-up API (client-side only, no mounted UI)
  // ---------------------------------------------------------------------------

  async createSignUp(params: {
    firstName: string;
    lastName: string;
    emailAddress: string;
    phoneNumber?: string;
    password: string;
    unsafeMetadata?: Record<string, unknown>;
  }): Promise<IClerkSignUpResult> {
    const clerk = await this.load();
    const signUp = await clerk.client.signUp.create({
      firstName: params.firstName,
      lastName: params.lastName,
      emailAddress: params.emailAddress,
      ...(params.phoneNumber ? { phoneNumber: params.phoneNumber } : {}),
      password: params.password,
      unsafeMetadata: params.unsafeMetadata
    });
    return this.mapSignUpResult(signUp);
  }

  async prepareSignUpEmailVerification(): Promise<IClerkSignUpResult> {
    const clerk = await this.load();
    const signUp = await clerk.client.signUp.prepareEmailAddressVerification({
      strategy: 'email_code'
    });
    return this.mapSignUpResult(signUp);
  }

  async attemptSignUpEmailVerification(code: string): Promise<IClerkSignUpResult> {
    const clerk = await this.load();
    const signUp = await clerk.client.signUp.attemptEmailAddressVerification({ code });

    if (signUp.status === 'complete' && signUp.createdSessionId) {
      await clerk.setActive({ session: signUp.createdSessionId });
      this.syncState(clerk);
    }

    return this.mapSignUpResult(signUp);
  }

  async prepareSignUpPhoneVerification(): Promise<IClerkSignUpResult> {
    const clerk = await this.load();
    const signUp = await clerk.client.signUp.preparePhoneNumberVerification({
      strategy: 'phone_code'
    });
    return this.mapSignUpResult(signUp);
  }

  async attemptSignUpPhoneVerification(code: string): Promise<IClerkSignUpResult> {
    const clerk = await this.load();
    const signUp = await clerk.client.signUp.attemptPhoneNumberVerification({ code });

    if (signUp.status === 'complete' && signUp.createdSessionId) {
      await clerk.setActive({ session: signUp.createdSessionId });
      this.syncState(clerk);
    }

    return this.mapSignUpResult(signUp);
  }

  async activateSignUpSession(): Promise<boolean> {
    const clerk = await this.load();
    const createdSessionId = clerk.client?.signUp?.createdSessionId;

    if (!createdSessionId) {
      return false;
    }

    await clerk.setActive({ session: createdSessionId });
    this.syncState(clerk);
    return true;
  }

  async signUpWithOAuth(params: {
    strategy: string;
    redirectUrl: string;
    redirectUrlComplete: string;
    unsafeMetadata?: Record<string, unknown>;
  }): Promise<void> {
    const clerk = await this.load();
    await clerk.client.signUp.authenticateWithRedirect({
      strategy: params.strategy,
      redirectUrl: params.redirectUrl,
      redirectUrlComplete: params.redirectUrlComplete,
      unsafeMetadata: params.unsafeMetadata
    });
  }

  async handleRedirectCallback(params: {
    signUpFallbackRedirectUrl?: string;
    signInFallbackRedirectUrl?: string;
    signUpForceRedirectUrl?: string;
    signInForceRedirectUrl?: string;
  }): Promise<void> {
    const clerk = await this.load();
    await clerk.handleRedirectCallback(params);
  }

  async getAvailableSocialProviders(): Promise<IClerkSocialProvider[]> {
    const clerk = await this.load();

    // The environment is a protected field on the Clerk instance populated
    // after clerk.load(). There is no public API to read enabled social
    // providers, so we access it via the runtime object.
    const env = (clerk as any).__unstable__environment ?? (clerk as any).environment;
    const strategies: string[] = env?.userSettings?.socialProviderStrategies ?? [];

    return strategies.map((strategy: string) => {
      const provider = strategy.replace(/^oauth_/, '');
      return {
        strategy,
        provider,
        label: provider.charAt(0).toUpperCase() + provider.slice(1)
      };
    });
  }

  getSignUpStatus(): IClerkSignUpResult | null {
    const signUp = this.clerkInstance?.client?.signUp;
    if (!signUp) {
      return null;
    }
    return this.mapSignUpResult(signUp);
  }

  private mapSignInResult(signIn: any): IClerkSignInResult {
    const factors: IClerkSecondFactor[] = Array.isArray(signIn.supportedSecondFactors)
      ? signIn.supportedSecondFactors.map((f: any) => ({
          strategy: f.strategy,
          ...(f.phoneNumberId ? { phoneNumberId: f.phoneNumberId } : {}),
          ...(f.safeIdentifier ? { safeIdentifier: f.safeIdentifier } : {})
        }))
      : [];

    return {
      status: signIn.status ?? null,
      createdSessionId: signIn.createdSessionId ?? null,
      supportedSecondFactors: factors
    };
  }

  private mapSignUpResult(signUp: any): IClerkSignUpResult {
    return {
      status: signUp.status ?? null,
      unverifiedFields: Array.isArray(signUp.unverifiedFields) ? signUp.unverifiedFields : [],
      missingFields: Array.isArray(signUp.missingFields) ? signUp.missingFields : [],
      createdSessionId: signUp.createdSessionId ?? null
    };
  }

  private async load(options?: IClerkMountOptions): Promise<any> {
    if (!this.publishableKey) {
      const message = 'Clerk publishable key is missing from the runtime auth configuration.';
      this.errorMessageState.set(message);
      throw new Error(message);
    }

    // Session-management callers (restoreSession, getToken) pass no options.
    // Reuse the existing instance without rechecking the signature so that
    // pending-state changes don't destroy the Clerk JS runtime mid-navigation.
    if (!options) {
      if (this.clerkInstance) {
        return this.clerkInstance;
      }
      if (this.clerkLoadPromise) {
        return this.clerkLoadPromise;
      }
    }

    const loadContext = this.resolveLoadContext(options);

    if (this.currentLoadSignature && this.currentLoadSignature !== loadContext.signature) {
      this.resetClerkInstance();
    }

    if (this.clerkInstance && this.currentLoadSignature === loadContext.signature) {
      return this.clerkInstance;
    }

    if (!this.clerkLoadPromise) {
      this.currentLoadSignature = loadContext.signature;
      this.clerkLoadPromise = this.createClerkInstance(loadContext);
    }

    return this.clerkLoadPromise;
  }

  private async createClerkInstance(loadContext: IClerkLoadContext): Promise<any> {
    try {
      const [{ Clerk }, { ClerkUI }] = await Promise.all([
        import('@clerk/clerk-js') as Promise<{ Clerk: ClerkConstructor }>,
        import('@clerk/ui/entry') as Promise<{ ClerkUI: unknown }>
      ]);
      const clerk = new Clerk(this.publishableKey);

      this.attachListeners(clerk);

      await clerk.load({
        signInUrl: loadContext.signInUrl,
        signUpUrl: loadContext.signUpUrl,
        afterSignInUrl: loadContext.afterSignInUrl,
        afterSignUpUrl: loadContext.afterSignUpUrl,
        allowedRedirectOrigins: [window.location.origin, ...this.allowedRedirectOrigins],
        ui: {
          ClerkUI
        }
      });

      this.clerkInstance = clerk;
      this.maybeActivateCreatedSession(clerk);
      this.syncState(clerk);
      this.isReadyState.set(true);
      this.errorMessageState.set(null);

      return clerk;
    } catch (error) {
      const message = this.getErrorMessage(error, 'Unable to load the Clerk sign-in experience.');

      this.currentLoadSignature = '';
      this.clerkLoadPromise = null;
      this.isReadyState.set(false);
      this.errorMessageState.set(message);

      throw new Error(message);
    }
  }

  private attachListeners(clerk: any): void {
    if (this.clerkListenersAttached) {
      return;
    }

    clerk.addListener(() => {
      this.maybeActivateCreatedSession(clerk);
      this.syncState(clerk);
    });

    clerk.on?.('status', (status: string) => {
      this.isReadyState.set(status === 'ready' || status === 'degraded');

      if (status === 'error') {
        this.errorMessageState.set('Clerk reported an error while loading the authentication flow.');
      }
    }, { notify: true });

    this.clerkListenersAttached = true;
  }

  async getClerkUserSnapshot(): Promise<IClerkUser | null> {
    const clerk = await this.load();
    const user = clerk.user;

    if (!user) {
      return null;
    }

    const emailAddresses: string[] = Array.isArray(user.emailAddresses)
      ? user.emailAddresses
          .map((entry: any) => entry?.emailAddress ?? entry)
          .filter((value: unknown): value is string => typeof value === 'string' && value.length > 0)
      : [];

    return {
      id: user.id ?? '',
      externalId: user.externalId ?? null,
      firstName: user.firstName ?? null,
      lastName: user.lastName ?? null,
      fullName: user.fullName ?? null,
      imageUrl: user.imageUrl ?? null,
      email: user.primaryEmailAddress?.emailAddress ?? emailAddresses[0] ?? '',
      emailAddresses,
      publicMetadata: this.readUnsafeMetadata(user.publicMetadata),
      unsafeMetadata: this.readUnsafeMetadata(user.unsafeMetadata)
    };
  }

  private syncState(clerk: any): void {
    this.isSignedInState.set(Boolean(clerk.session));
    this.currentUserIdState.set(clerk.user?.id ?? null);
    this.currentSessionIdState.set(clerk.session?.id ?? null);
    this.currentUnsafeMetadataState.set(this.readUnsafeMetadata(clerk.user?.unsafeMetadata));
    this.isActorSessionState.set(clerk.session?.actor != null);
  }

  private maybeActivateCreatedSession(clerk: any): void {
    const isSignInComplete = clerk.client?.signIn?.status === 'complete';
    const isSignUpComplete = clerk.client?.signUp?.status === 'complete';

    const createdSessionId = isSignInComplete
      ? clerk.client.signIn.createdSessionId
      : isSignUpComplete
        ? clerk.client.signUp.createdSessionId
        : null;

    if (clerk.session || !createdSessionId || this.isActivatingCreatedSession) {
      return;
    }

    const redirectUrl = this.getCurrentPostAuthRedirectUrl() ?? this.buildAppUrl('/dashboard');

    this.isActivatingCreatedSession = true;

    void clerk.setActive({ session: createdSessionId, ...(redirectUrl ? { redirectUrl } : {}) })
      .finally(() => {
        this.isActivatingCreatedSession = false;
      });
  }

  private resolveLoadContext(options?: IClerkMountOptions): IClerkLoadContext {
    const signInUrl = this.stripHash(options?.signInUrl?.trim() || this.buildAppUrl('/login'));
    const signUpUrl = this.stripHash(options?.signUpUrl?.trim() || this.buildAppUrl('/signup'));
    const afterSignInUrl = this.resolveAfterSignInUrl(options);
    const afterSignUpUrl = this.resolveAfterSignUpUrl(options);

    return {
      signInUrl,
      signUpUrl,
      afterSignInUrl,
      afterSignUpUrl,
      signature: `${signInUrl}||${signUpUrl}||${afterSignInUrl}||${afterSignUpUrl}`
    };
  }

  private resolveMountOptions(mode: ClerkMode, options?: IClerkMountOptions): IResolvedClerkMountOptions {
    const signInUrl = options?.signInUrl?.trim() || this.buildAppUrl('/login');
    const signUpUrl = options?.signUpUrl?.trim() || this.buildAppUrl('/signup');
    const defaultRedirectUrl = mode === 'sign-in' ? signInUrl : signUpUrl;

    return {
      routing: options?.routing ?? 'path',
      signInUrl,
      signUpUrl,
      fallbackRedirectUrl: options?.fallbackRedirectUrl?.trim() || defaultRedirectUrl,
      forceRedirectUrl: options?.forceRedirectUrl?.trim() || undefined
    };
  }

  private resetRuntimeState(): void {
    this.resetClerkInstance();
    this.errorMessageState.set(null);
  }

  private clearBrowserState(): void {
    this.clearStorage(window.localStorage, CLERK_STORAGE_PREFIXES);
    this.clearStorage(window.sessionStorage, CLERK_STORAGE_PREFIXES, CLERK_SESSION_STORAGE_KEYS);
    this.clearMatchingCookies(CLERK_COOKIE_PREFIXES);
  }

  private clearStorage(
    storage: Storage,
    prefixes: readonly string[],
    exactKeys: readonly string[] = []
  ): void {
    const keysToRemove: string[] = [];

    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);

      if (key && (exactKeys.includes(key) || prefixes.some((prefix) => key.startsWith(prefix)))) {
        keysToRemove.push(key);
      }
    }

    for (const key of exactKeys) {
      if (!keysToRemove.includes(key)) {
        keysToRemove.push(key);
      }
    }

    for (const key of keysToRemove) {
      storage.removeItem(key);
    }
  }

  private clearMatchingCookies(prefixes: readonly string[]): void {
    const cookieNames = document.cookie
      .split(';')
      .map((cookie) => cookie.trim().split('=')[0]?.trim())
      .filter((name): name is string => Boolean(name));

    for (const name of cookieNames) {
      if (!prefixes.some((prefix) => name.startsWith(prefix))) {
        continue;
      }

      this.expireCookie(name);
    }
  }

  private expireCookie(name: string): void {
    const expires = 'Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie = `${name}=; expires=${expires}; path=/`;

    if (window.location.hostname.includes('.')) {
      document.cookie = `${name}=; expires=${expires}; path=/; domain=${window.location.hostname}`;
    }
  }

  private resetClerkInstance(): void {
    this.clerkInstance = null;
    this.clerkLoadPromise = null;
    this.clerkListenersAttached = false;
    this.currentLoadSignature = '';
    this.isReadyState.set(false);
    this.isSignedInState.set(false);
    this.currentUserIdState.set(null);
    this.currentSessionIdState.set(null);
    this.currentUnsafeMetadataState.set({});
  }

  private readUnsafeMetadata(metadata: unknown): Record<string, unknown> {
    if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
      return {};
    }

    return metadata as Record<string, unknown>;
  }

  private buildAppUrl(path: string): string {
    return new URL(path, document.baseURI).toString();
  }

  private isGuestRoute(pathname: string): boolean {
    return pathname === '/login'
      || pathname === '/signup'
      || pathname === '/custom-signup';
  }

  private resolveAfterSignInUrl(options?: IClerkMountOptions): string {
    return options?.forceRedirectUrl?.trim()
      || options?.fallbackRedirectUrl?.trim()
      || this.getCurrentPostAuthRedirectUrl()
      || this.buildAppUrl('/profile');
  }

  private resolveAfterSignUpUrl(options?: IClerkMountOptions): string {
    return options?.forceRedirectUrl?.trim()
      || options?.fallbackRedirectUrl?.trim()
      || this.getCurrentPostAuthRedirectUrl()
      || this.buildAppUrl('/profile');
  } 

  private stripHash(url: string): string {
    const parsedUrl = new URL(url, document.baseURI);
    parsedUrl.hash = '';
    return parsedUrl.toString();
  }

  private getErrorMessage(error: unknown, fallback: string): string {
    // Clerk SDK throws ClerkAPIResponseError with an `errors` array
    const clerkErrors = (error as any)?.errors;
    if (Array.isArray(clerkErrors) && clerkErrors.length > 0) {
      const first = clerkErrors[0];
      const clerkMessage = first?.longMessage ?? first?.long_message ?? first?.message;
      if (typeof clerkMessage === 'string' && clerkMessage.trim().length > 0) {
        return clerkMessage.trim();
      }
    }

    if (error instanceof Error && error.message.trim().length > 0) {
      return error.message.trim();
    }

    if (typeof error === 'string' && error.trim().length > 0) {
      return error.trim();
    }

    return fallback;
  }
}